/**
 * Aspect Ratio segmented control.
 *
 * 5 つのボタン（`16:9` / `4:3` / `1:1` / `9:16` / `Custom`）から 1 つを選ぶ
 * segmented UI。`Custom` 選択時のみ、直下に `Width × Height`（px 整数）の
 * 入力フィールドを展開表示する。
 * 設計書 FR-01 / 10.2 / 10.4。プロトタイプ `docs/prototype.html` の
 * `.segmented` マークアップ + active クラス挙動に合わせる。
 *
 * 責務:
 * - クリックで `store.set({ aspectRatio })`
 * - `Custom` 選択時のみ W × H 入力欄を可視化
 * - W / H 入力コミット時に `store.set({ customAspect })`
 * - `store.subscribe` で外部変更（プリセット読み込み等）にも追従
 * - マウント時はルート要素に section-label + segmented + custom inputs を追加
 */

import type { AppState, AspectRatio, Store } from '../../state';

/** 表示順は設計書 10.2（プロトタイプと同順）。末尾に 'custom' を追加。 */
const RATIOS: readonly AspectRatio[] = ['16:9', '4:3', '1:1', '9:16', 'custom'];

/** Custom width / height 入力の許容範囲（px、整数）。spec §6 に合わせる。 */
const CUSTOM_MIN = 100;
const CUSTOM_MAX = 10000;

/** ボタン表示文字列。`'custom'` は `'Custom'` と表示する（他は ratio 文字列そのまま）。 */
function labelFor(ratio: AspectRatio): string {
  return ratio === 'custom' ? 'Custom' : ratio;
}

/** 値を整数化 + 範囲クランプ。NaN のときは fallback を返す。 */
function clampInt(raw: string, fallback: number): number {
  const n = Number.parseInt(raw, 10);
  if (Number.isNaN(n)) return fallback;
  return Math.min(CUSTOM_MAX, Math.max(CUSTOM_MIN, n));
}

/**
 * Aspect Ratio セクションをマウントする。
 *
 * @param root - 挿入先コンテナ（通常は `.controls` 配下の新規 `<div>`）
 * @param store - グローバルストア
 * @returns teardown 関数。呼ぶと DOM・subscribe を解除する。
 */
export function mountAspectRatio(root: HTMLElement, store: Store<AppState>): () => void {
  const section = document.createElement('div');

  const label = document.createElement('div');
  label.className = 'section-label';
  label.textContent = 'Aspect Ratio';
  section.appendChild(label);

  const segmented = document.createElement('div');
  segmented.className = 'segmented';
  section.appendChild(segmented);

  // ボタンと aspectRatio 値の対応表（購読時に active 切替で参照）。
  const buttons = new Map<AspectRatio, HTMLButtonElement>();

  for (const ratio of RATIOS) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.dataset.ratio = ratio;
    btn.textContent = labelFor(ratio);
    btn.addEventListener('click', () => {
      // すでに選択中なら no-op（不要な再計算を避ける）
      if (store.get().aspectRatio === ratio) return;
      store.set({ aspectRatio: ratio });
    });
    segmented.appendChild(btn);
    buttons.set(ratio, btn);
  }

  // ── Custom W × H 入力欄（aspectRatio === 'custom' 時のみ可視）──
  const customBox = document.createElement('div');
  customBox.className = 'custom-aspect-inputs';

  const widthField = document.createElement('label');
  widthField.className = 'custom-aspect-field';
  const widthLabel = document.createElement('span');
  widthLabel.textContent = 'W (px)';
  const widthInput = document.createElement('input');
  widthInput.type = 'number';
  widthInput.min = String(CUSTOM_MIN);
  widthInput.max = String(CUSTOM_MAX);
  widthInput.step = '1';
  widthInput.inputMode = 'numeric';
  widthField.appendChild(widthLabel);
  widthField.appendChild(widthInput);

  const heightField = document.createElement('label');
  heightField.className = 'custom-aspect-field';
  const heightLabel = document.createElement('span');
  heightLabel.textContent = 'H (px)';
  const heightInput = document.createElement('input');
  heightInput.type = 'number';
  heightInput.min = String(CUSTOM_MIN);
  heightInput.max = String(CUSTOM_MAX);
  heightInput.step = '1';
  heightInput.inputMode = 'numeric';
  heightField.appendChild(heightLabel);
  heightField.appendChild(heightInput);

  customBox.appendChild(widthField);
  customBox.appendChild(heightField);
  section.appendChild(customBox);

  // 初期値を反映。
  {
    const { customAspect } = store.get();
    widthInput.value = String(customAspect.width);
    heightInput.value = String(customAspect.height);
  }

  /**
   * W / H のうち片方をコミットする。
   * - 空文字・NaN のときは現在の store 値に戻す（入力欄の value も同期）
   * - クランプ後、現在値と異なる場合のみ store.set（再入を避ける）
   */
  const commit = (which: 'width' | 'height', input: HTMLInputElement): void => {
    const current = store.get().customAspect;
    const next = clampInt(input.value, current[which]);
    // 表示は常にクランプ済み整数に揃える（ユーザー入力が範囲外の場合に追従）。
    input.value = String(next);
    if (next === current[which]) return;
    store.set({ customAspect: { ...current, [which]: next } });
  };

  const onWidthChange = (): void => commit('width', widthInput);
  const onHeightChange = (): void => commit('height', heightInput);

  // `change` は blur または Enter 押下で発火する（input type=number の既定挙動）。
  // 連続入力中に state を汚さないため、`input` ではなく `change` を使う。
  widthInput.addEventListener('change', onWidthChange);
  heightInput.addEventListener('change', onHeightChange);

  /** active クラスと custom 入力欄の可視状態を state に同期する。 */
  const sync = (current: AspectRatio): void => {
    for (const [ratio, btn] of buttons) {
      btn.classList.toggle('active', ratio === current);
    }
    // `display: none` で非表示。スクロール可能領域を詰めるためにレイアウトから除外する。
    customBox.style.display = current === 'custom' ? 'flex' : 'none';
  };

  sync(store.get().aspectRatio);

  const unsubscribe = store.subscribe((state, prev) => {
    if (state.aspectRatio !== prev.aspectRatio) {
      sync(state.aspectRatio);
    }
    // 外部（preset / reset 等）からの customAspect 変更を input に反映する。
    // ユーザー入力 → store.set → この subscribe の流れで同じ値に上書きされても
    // value の文字列比較で no-op に落ちるため、caret ジャンプ等は起きない。
    if (state.customAspect !== prev.customAspect) {
      const w = String(state.customAspect.width);
      const h = String(state.customAspect.height);
      if (widthInput.value !== w) widthInput.value = w;
      if (heightInput.value !== h) heightInput.value = h;
    }
  });

  root.appendChild(section);

  return (): void => {
    unsubscribe();
    widthInput.removeEventListener('change', onWidthChange);
    heightInput.removeEventListener('change', onHeightChange);
    section.remove();
  };
}
